import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { api, connectSse, getJwtExpMs, refreshTokensIfPossible } from '../lib/api'
import { clearCustomerMemoDraftStorage } from '../lib/customerMemoDraftStorage'
import { clearSessionQueryCache } from '../lib/queryClient'
import { hasAnyCapability, hasAnyPermission, hasAnyRole, hasHealthAnalysisAccess, hasInsuranceNewsAccess, Permissions, Roles } from './rbac'

const AuthContext = createContext(undefined)

function normalizeMePayload(raw) {
    if (!raw || typeof raw !== 'object') return raw
    const payload = { ...raw }
    if (payload.service_profile != null && payload.serviceProfile == null) {
        payload.serviceProfile = payload.service_profile
    }
    if (payload.shell_nav_org_logo_file_id != null && payload.shellNavOrgLogoFileId == null) {
        payload.shellNavOrgLogoFileId = payload.shell_nav_org_logo_file_id
    }
    if (payload.serviceProfile == null) {
        payload.serviceProfile = 'FULL_SERVICE'
    }
    if (!Array.isArray(payload.capabilities)) {
        payload.capabilities = []
    }
    if (!Array.isArray(payload.visibleMenuKeys)) {
        const snake = payload.visible_menu_keys
        if (Array.isArray(snake)) {
            payload.visibleMenuKeys = snake
        }
    }
    return payload
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [initializing, setInitializing] = useState(true)
    const sseRef = useRef(null)
    const refreshTimerRef = useRef(null)

    const clearRefreshTimer = useCallback(() => {
        if (refreshTimerRef.current) {
            try { clearTimeout(refreshTimerRef.current) } catch { }
            refreshTimerRef.current = null
        }
    }, [])

    const scheduleProactiveRefresh = useCallback(() => {
        clearRefreshTimer()
        const token = localStorage.getItem('accessToken')
        const refreshToken = localStorage.getItem('refreshToken')
        if (!token || !refreshToken) return

        // 탭이 숨김 상태(백그라운드/PC 잠금 등)일 때는 proactive refresh를 멈춘다.
        // - 백그라운드 타이머 스로틀링으로 어차피 타이밍이 흔들릴 수 있고,
        // - 잠금 중 불필요한 세션 연장을 줄이기 위함.
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            return
        }

        const expMs = getJwtExpMs(token)
        if (!expMs) return

        // 만료 2분 전 갱신(업무 시간 탭 상시 유지 + 클럭 오차 대비)
        const refreshAt = expMs - 2 * 60 * 1000
        const delay = Math.max(0, refreshAt - Date.now())
        refreshTimerRef.current = setTimeout(async () => {
            const refreshed = await refreshTokensIfPossible()
            if (refreshed?.accessToken) {
                scheduleProactiveRefresh()
                return
            }
            // refresh 실패(CSP·네트워크·만료 토큰 등) 시 delay=0으로 즉시 재시도하면 /auth/refresh 폭주
            const cur = localStorage.getItem('accessToken')
            const expMs = getJwtExpMs(cur)
            if (expMs && Date.now() >= expMs) {
                return
            }
            refreshTimerRef.current = setTimeout(() => scheduleProactiveRefresh(), 60_000)
        }, delay)
    }, [clearRefreshTimer])

    // 탭이 다시 활성화되면 즉시 refresh(필요 시) 후 타이머 재스케줄
    useEffect(() => {
        const onVisibilityChange = async () => {
            try {
                if (typeof document === 'undefined') return
                if (document.visibilityState === 'hidden') {
                    clearRefreshTimer()
                    return
                }

                // visible로 돌아오면: 만료/임박 가능성이 높으니 한번 refresh 시도
                const token = localStorage.getItem('accessToken')
                const refreshToken = localStorage.getItem('refreshToken')
                if (!token || !refreshToken) return

                const expMs = getJwtExpMs(token)
                // exp를 못 읽더라도, 401은 인터셉터에서 refresh 처리되므로 여기서는 스케줄만 재설정
                if (expMs && Date.now() >= (expMs - 2 * 60 * 1000)) {
                    await refreshTokensIfPossible()
                }
                scheduleProactiveRefresh()
            } catch {
                // ignore
            }
        }
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', onVisibilityChange)
        }
        return () => {
            try {
                if (typeof document !== 'undefined') {
                    document.removeEventListener('visibilitychange', onVisibilityChange)
                }
            } catch { }
        }
    }, [clearRefreshTimer, scheduleProactiveRefresh])

    const loadUserInfo = useCallback(async () => {
        try {
            // 403 시 전역 인터셉터가 /forbidden 으로 보내 세션 복구가 깨질 수 있어 조용히 호출
            const me = await api.get('/users/me', { __silent: true })
            const payload = normalizeMePayload(
                (me?.data && me.data.data !== undefined) ? me.data.data : me.data,
            )
            localStorage.setItem('auth.user', JSON.stringify(payload))
            setUser(payload)
        } catch {
            // 토큰이 없거나 만료된 경우 - 아무것도 하지 않음
        }
    }, [])

    const startSse = useCallback(() => {
        try {
            // 기존 연결이 있으면 정리
            if (sseRef.current) {
                try { sseRef.current.close() } catch { }
                sseRef.current = null
            }
            const es = connectSse(() => localStorage.getItem('accessToken'), (payload) => {
                try {
                    const title = payload?.title || '새 알림이 도착했습니다.'
                    const summary = payload?.summary || payload?.message || ''
                    const msg = summary ? `${title} - ${summary}` : title
                    window?.toast?.show?.(msg, 'info', 5000)
                } catch { }
                try { window.dispatchEvent(new CustomEvent('notifications:new', { detail: payload })) } catch { }
            }, (payload) => {
                try { window.dispatchEvent(new CustomEvent('notifications:dismiss', { detail: payload })) } catch { }
            })
            sseRef.current = es
        } catch {
        }
    }, [])

    useEffect(() => {
        let cancelled = false
        ; (async () => {
                const accessToken = localStorage.getItem('accessToken')
                const refreshToken = localStorage.getItem('refreshToken')
                const userData = localStorage.getItem('auth.user')

                // accessToken이 있으면 항상 /users/me로 최신화한다.
                // 예전에 저장된 auth.user(JSON)에는 capabilities·serviceProfile 등이 없을 수 있어
                // RequireCapability(HEALTH_ANALYSIS) 등에서 403이 나는 문제가 생긴다.
                if (accessToken) {
                    if (userData) {
                        try {
                            setUser(JSON.parse(userData))
                        } catch {
                            /* loadUserInfo로 대체 */
                        }
                    }
                    await loadUserInfo()
                } else if (refreshToken) {
                    await loadUserInfo()
                }
                if (!cancelled) setInitializing(false)

                // 로그인/복원된 경우 SSE 연결
                const token = localStorage.getItem('accessToken')
                if (!cancelled && token) {
                    startSse()
                    scheduleProactiveRefresh()
                }
            })()
        return () => {
            cancelled = true
            clearRefreshTimer()
            try {
                if (sseRef.current) {
                    sseRef.current.close()
                    sseRef.current = null
                }
            } catch { }
        }
    }, [startSse, scheduleProactiveRefresh, clearRefreshTimer, loadUserInfo])

    const finalizeLoginWithTokens = useCallback(async (accessToken, refreshToken) => {
        if (!accessToken || !refreshToken) throw new Error('invalid_tokens')
        clearSessionQueryCache()
        // 토큰 저장
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', refreshToken)

        // 로그인 성공 후 사용자 정보 가져오기
        const me = await api.get('/users/me', { __silent: true })
        const payload = normalizeMePayload(
            (me?.data && me.data.data !== undefined) ? me.data.data : me.data,
        )
        localStorage.setItem('auth.user', JSON.stringify(payload))
        setUser(payload)

        // 로그인 직후 SSE 연결 시작
        startSse()
        scheduleProactiveRefresh()
    }, [startSse, scheduleProactiveRefresh])

    // 외부(SDK/브리지 등)에서 토큰만 받아 로그인 완료 처리할 때 사용
    const loginWithTokens = useCallback(async ({ accessToken, refreshToken }) => {
        return await finalizeLoginWithTokens(accessToken, refreshToken)
    }, [finalizeLoginWithTokens])

    const login = useCallback(async (email, password) => {
        try {
            // 로그인 요청
            const res = await api.post('/auth/login', { username: email, password: password })
            const { accessToken, refreshToken } = (res?.data && res.data.data) ? res.data.data : res.data
            await finalizeLoginWithTokens(accessToken, refreshToken)
        } catch (err) {
            // 실패 시 모든 토큰 비우고 에러 전파
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('auth.user')
            clearSessionQueryCache()
            setUser(null)
            throw err
        }
    }, [finalizeLoginWithTokens])

    const loginWithGoogle = useCallback(async (idToken) => {
        try {
            const res = await api.post('/auth/social/google/login', { idToken })
            const { accessToken, refreshToken } = (res?.data && res.data.data) ? res.data.data : res.data
            await finalizeLoginWithTokens(accessToken, refreshToken)
        } catch (err) {
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('auth.user')
            clearSessionQueryCache()
            setUser(null)
            throw err
        }
    }, [finalizeLoginWithTokens])

    const loginWithKakao = useCallback(async (accessTokenFromKakao) => {
        try {
            const res = await api.post('/auth/social/kakao/login', { accessToken: accessTokenFromKakao })
            const { accessToken, refreshToken } = (res?.data && res.data.data) ? res.data.data : res.data
            await finalizeLoginWithTokens(accessToken, refreshToken)
        } catch (err) {
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('auth.user')
            clearSessionQueryCache()
            setUser(null)
            throw err
        }
    }, [finalizeLoginWithTokens])

    const loginWithNaver = useCallback(async (accessTokenFromNaver) => {
        try {
            const res = await api.post('/auth/social/naver/login', { accessToken: accessTokenFromNaver })
            const { accessToken, refreshToken } = (res?.data && res.data.data) ? res.data.data : res.data
            await finalizeLoginWithTokens(accessToken, refreshToken)
        } catch (err) {
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('auth.user')
            clearSessionQueryCache()
            setUser(null)
            throw err
        }
    }, [finalizeLoginWithTokens])

    const loginWithNaverCode = useCallback(async ({ code, state, redirectUri }) => {
        try {
            const res = await api.post('/auth/social/naver/login', { code, state, redirectUri })
            const { accessToken, refreshToken } = (res?.data && res.data.data) ? res.data.data : res.data
            await finalizeLoginWithTokens(accessToken, refreshToken)
        } catch (err) {
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('auth.user')
            clearSessionQueryCache()
            setUser(null)
            throw err
        }
    }, [finalizeLoginWithTokens])

    // 토큰 리프레시 시 SSE를 최신 토큰으로 재연결
    useEffect(() => {
        const onRefreshed = () => {
            try {
                const token = localStorage.getItem('accessToken')
                if (token) startSse()
            } catch { }
            // 권한/역할 변경이 role_permissions 기반으로 반영되도록, 토큰 갱신 시점에 사용자 정보를 재조회한다.
            // (서버는 /users/me 응답에 permissions를 포함한다)
            try { loadUserInfo() } catch { }
            try { scheduleProactiveRefresh() } catch { }
        }
        window.addEventListener('auth:tokenRefreshed', onRefreshed)
        return () => window.removeEventListener('auth:tokenRefreshed', onRefreshed)
    }, [startSse, scheduleProactiveRefresh, loadUserInfo])

    // 강제 로그아웃 이벤트(리프레시 실패 등) 처리: SSE 종료 + 상태 초기화
    useEffect(() => {
        const onForceLogout = () => {
            clearRefreshTimer()
            try {
                if (sseRef.current) {
                    sseRef.current.close()
                    sseRef.current = null
                }
            } catch { }
            try {
                localStorage.removeItem('accessToken')
                localStorage.removeItem('refreshToken')
                localStorage.removeItem('auth.user')
            } catch { }
            clearCustomerMemoDraftStorage()
            clearSessionQueryCache()
            setUser(null)
        }
        window.addEventListener('auth:logout', onForceLogout)
        return () => window.removeEventListener('auth:logout', onForceLogout)
    }, [clearRefreshTimer])

    const logout = useCallback(async () => {
        // 서버에 별도 logout 엔드포인트가 없는 환경이 있어(404),
        // 클라이언트 로그아웃은 로컬 토큰/상태 정리로만 처리한다.
        // (리프레시 토큰 무효화가 필요하면 BE에 /auth/logout 구현 후 여기서 호출하도록 확장)
        clearRefreshTimer()
        try {
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('auth.user')
        } catch { }
        clearCustomerMemoDraftStorage()
        clearSessionQueryCache()
        try {
            if (sseRef.current) {
                sseRef.current.close()
                sseRef.current = null
            }
        } catch { }
        setUser(null)
    }, [clearRefreshTimer])

    const value = useMemo(() => ({
        user,
        isAuthenticated: !!user,
        initializing,
        login,
        loginWithTokens,
        loginWithGoogle,
        loginWithKakao,
        loginWithNaver,
        loginWithNaverCode,
        logout,
        refreshUser: loadUserInfo,
        hasAnyRole: (roles) => hasAnyRole(user, roles),
        hasAnyPermission: (perms) => hasAnyPermission(user, perms),
        hasAnyCapability: (capabilities) => hasAnyCapability(user, capabilities),
        hasHealthAnalysisAccess: () => hasHealthAnalysisAccess(user),
        hasInsuranceNewsAccess: () => hasInsuranceNewsAccess(user),
        Roles,
        Permissions,
    }), [user, initializing, login, loginWithTokens, loginWithGoogle, loginWithKakao, loginWithNaver, loginWithNaverCode, logout, loadUserInfo])
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be used within AuthProvider')
    return ctx
}

export function RequireRole({ roles, children, fallback = null }) {
    const { isAuthenticated, user } = useAuth()
    if (!isAuthenticated) return null
    // 운영자는 모든 기능 가능(요구사항): 라우트/컴포넌트 레벨 Role Gate를 항상 통과시킨다.
    if (hasAnyRole(user, [Roles.OPERATOR])) return children
    const allowed = hasAnyRole(user, roles)
    if (!allowed) return fallback
    return children
}

export function RequirePermission({ permissions, children, fallback = null }) {
    const { isAuthenticated, user } = useAuth()
    if (!isAuthenticated) return null
    if (hasAnyRole(user, [Roles.OPERATOR])) return children
    const orgScopedPermissions = [Permissions.ORG_MANAGE, Permissions.USER_INVITE, Permissions.ORG_BANNER_MANAGE]
    const requiresOrgScope = (permissions || []).some((permission) => orgScopedPermissions.includes(permission))
    if (requiresOrgScope && String(user?.orgType || '').toUpperCase() === 'PERSONAL') return fallback
    const allowed = hasAnyPermission(user, permissions)
    if (!allowed) return fallback
    return children
}

export function RequireCapability({ capabilities, children, fallback = null }) {
    const { isAuthenticated, user, hasAnyCapability } = useAuth()
    if (!isAuthenticated) return null
    if (hasAnyRole(user, [Roles.OPERATOR])) return children
    const allowed = hasAnyCapability(capabilities)
    if (!allowed) return fallback
    return children
}

export function RequireVisibleMenuKey({ menuKey, children, fallback = null }) {
    const { isAuthenticated, user } = useAuth()
    if (!isAuthenticated) return null
    if (hasAnyRole(user, [Roles.OPERATOR])) return children
    const raw = user?.visibleMenuKeys ?? user?.visible_menu_keys
    if (!Array.isArray(raw)) return children
    if (!menuKey || raw.includes(menuKey)) return children
    return fallback
}

/** 진료정보분석: capability 또는 풀서비스 조직(REQUEST/FULL_SERVICE) 기준 */
export function RequireHealthAnalysisAccess({ children, fallback = null }) {
    const { isAuthenticated, hasHealthAnalysisAccess } = useAuth()
    if (!isAuthenticated) return null
    if (!hasHealthAnalysisAccess()) return fallback
    return children
}

/** 보험뉴스·보험사 소식지: capability 또는 풀서비스 조직 동일 정책 */
export function RequireInsuranceNewsAccess({ children, fallback = null }) {
    const { isAuthenticated, hasInsuranceNewsAccess } = useAuth()
    if (!isAuthenticated) return null
    if (!hasInsuranceNewsAccess()) return fallback
    return children
}
